import { Dialog } from '@headlessui/react'
import { Progress } from 'antd'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import CurrencyInput from 'react-currency-input-field'
import { useNavigate } from 'react-router-dom'

import {
  useReportUrlDispatch,
  useReportUrlState,
} from '@Utils/context/modal/useReportUrl'
import TextInput from '@Utils/inputs/text-input'

import ClockIcon from '../../../assets/images/icons/clock-fast-forward.svg?react'
import AlertCircleIcon from '../../../assets/images/icons/error-alert-circle.svg?react'
import SuccessIcon from '../../../assets/images/icons/success-icon.svg?react'
import XIcon from '../../../assets/images/icons/x-close-filter.svg?react'
import PrimaryButton from '../../../utils/buttons/primary-button'
import ManageTableCheckbox from '../../../utils/forms/checkbox/manage-table-checkbox'
import ErrorMessage from '../../../utils/forms/error/error-message'
import RadioInput from '../../radio-input'

const STATEMENTS = [
  {
    key: 'statementOne',
    label:
      'I clicked the link and opened it in my browser. I can see the product page in my browser.',
  },
  {
    key: 'statementTwo',
    label: 'On the product page, the price is shown.',
  },
  {
    key: 'statementThree',
    label: 'The page is not a "404 Not Found" page.',
  },
  {
    key: 'statementFour',
    label: 'This link is recently updated on Prisync.',
  },
] as const

type AddReportUrl = {
  reason?: string
  details?: string
  id?: number
  urlId?: number
}

function ReportUrlModal(): JSX.Element {
  const { urlInfo, isOpen, isSuccess, isLoading, isError, error } =
    useReportUrlState()
  const { close, reportUrl } = useReportUrlDispatch()

  const [step, setStep] = useState<number>(1)
  const [isAlreadyReported, setIsAlreadyReported] = useState<any>()
  const navigate = useNavigate()

  const [isStatementsChecked, setIsStatementsChecked] = useState<{
    statementOne: boolean
    statementTwo: boolean
    statementThree: boolean
    statementFour: boolean
  }>({
    statementOne: false,
    statementTwo: false,
    statementThree: false,
    statementFour: false,
  })

  const initialValues = {
    problem: '',
    price: '',
    other: '',
  }
  let response: any

  const checkedCount = Object.values(isStatementsChecked).filter(Boolean).length
  const totalStatements = STATEMENTS.length
  const remainingStatements = totalStatements - checkedCount

  useEffect(() => {
    if (isSuccess === true) {
      setStep(3)
    }
  }, [isSuccess])

  useEffect(() => {
    if (isOpen === false) {
      setIsStatementsChecked({
        statementOne: false,
        statementTwo: false,
        statementThree: false,
        statementFour: false,
      })
    }
  }, [isOpen])

  const onClose = () => {
    setStep(1)
    close()
  }

  const onSubmit = async (e: any) => {
    const createRequest: AddReportUrl = urlInfo as AddReportUrl

    if (e.problem === 'option1') {
      createRequest.reason = '1'
    }
    if (e.problem === 'option2') {
      createRequest.reason = '2'
      createRequest.details = e.price
    }
    if (e.problem === 'option3') {
      createRequest.reason = '3'
    }
    if (e.problem === 'option4') {
      createRequest.reason = '4'
      createRequest.details = e.other
    }

    try {
      response = await reportUrl(createRequest).unwrap()
      setIsAlreadyReported(response)
    } catch (error) {}
  }
  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        background: 'rgba(41, 41, 41, 0.7)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Dialog.Panel className="relative border rounded-xl shadow-xl bg-white-100 ">
        <div className="flex flex-col p-6 ">
          <button
            className="absolute right-8 active:bg-gray-50 border-0 p-2 rounded-full"
            onClick={onClose}
          >
            <XIcon />
          </button>
          {step === 1 && (
            <>
              <div className="mt-10 flex flex-col w-96">
                <h1 className="font-medium text-lg text-gray-900 mb-2">
                  Please make sure all statements are checked
                </h1>
                <p className="font-normal text-sm text-gray-500 mb-4">
                  To make sure this link shows the correct product page, you
                  need to confirm{' '}
                  <span className="font-medium text-gray-700">
                    all {totalStatements}
                  </span>{' '}
                  statements below.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <Progress
                    percent={(checkedCount / totalStatements) * 100}
                    showInfo={false}
                    strokeColor="#3E54B8"
                    railColor="#EAECF0"
                    size={{ height: 6 }}
                    className="flex-1 leading-none"
                  />
                  <span className="font-medium text-sm text-primary-600 whitespace-nowrap">
                    {checkedCount}/{totalStatements} completed
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {STATEMENTS.map(({ key, label }) => {
                    const isChecked = isStatementsChecked[key]
                    return (
                      <div
                        key={key}
                        className={`inline-flex items-center rounded-lg border px-3.5 py-3 transition-colors ${
                          isChecked
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-gray-300 bg-white-100'
                        }`}
                      >
                        <ManageTableCheckbox
                          handleCheckboxChange={() =>
                            setIsStatementsChecked({
                              ...isStatementsChecked,
                              [key]: !isChecked,
                            })
                          }
                          isChecked={isChecked}
                        />
                        <p
                          className={
                            isChecked
                              ? 'font-medium text-sm text-gray-900'
                              : 'font-normal text-sm text-gray-700'
                          }
                        >
                          {label}
                        </p>
                      </div>
                    )
                  })}
                </div>
                <div className="h-5 mt-3">
                  {remainingStatements > 0 && (
                    <div className="flex items-center gap-1.5">
                      <AlertCircleIcon className="w-[15px] h-[15px] shrink-0 [&_path]:stroke-warning-600" />
                      <span className="text-sm text-warning-600">
                        {remainingStatements} more statement
                        {remainingStatements !== 1 ? 's' : ''} needed to
                        continue
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-row mt-4 w-full">
                <button
                  onClick={onClose}
                  className="border border-solid border-gray-300 shadow-sm rounded-lg h-11 w-1/3 mr-1.5 active:bg-gray-100"
                >
                  <span className="font-medium text-base text-gray-700 ">
                    Cancel
                  </span>
                </button>
                <button
                  disabled={checkedCount < totalStatements}
                  type="button"
                  onClick={() => setStep(2)}
                  className="border border-solid disabled:bg-gray-300 disabled:border-gray-300 active:border-error-600 bg-error-600 active:bg-error-700 shadow-sm rounded-lg h-11 w-2/3 ml-1.5"
                >
                  <span className="font-medium text-base text-white-100 ">
                    Continue
                  </span>
                </button>
              </div>
            </>
          )}
          {step === 2 && (
            <div className="mt-10">
              <h1 className="font-medium text-lg text-gray-900 mb-6 ">
                Broken URL report
              </h1>
              <p className="font-medium text-sm text-gray-900 mb-5 ">
                Just one more second. What's the problem of this link?
              </p>
              <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {({ values, setValues }) => (
                  <Form>
                    <RadioInput
                      onChange={(val) => setValues({ ...values, problem: val })}
                      options={[
                        {
                          text: 'Price is available on the link but not shown on Prisync.',
                          value: 'option1',
                        },
                        {
                          text: 'Price on Prisync is different than the price on the link.',
                          value: 'option2',
                          renderOnChecked: (
                            <div className="pl-5 -mt-2 pb-2 flex justify-between">
                              Type the price on the link:
                              <CurrencyInput
                                name="price"
                                allowNegativeValue={false}
                                decimalScale={2}
                                groupSeparator=","
                                disableGroupSeparators
                                decimalSeparator="."
                                className="border border-solid text-gray-900 bg-white-100 border-gray-300 rounded-md"
                                onValueChange={(
                                  _value,
                                  _name,
                                  currencyValues,
                                ) => {
                                  setValues({
                                    ...values,
                                    price:
                                      currencyValues?.float !== null &&
                                      currencyValues?.float !== undefined
                                        ? String(currencyValues.float)
                                        : '',
                                  })
                                }}
                              />
                            </div>
                          ),
                        },
                        {
                          text: 'Stock info is incorrect.',
                          value: 'option3',
                        },
                        {
                          text: 'Other. ',
                          value: 'option4',
                          renderOnChecked: (
                            <div className="pl-5 -mt-3 pb-2 flex flex-col">
                              (Please describe the issue with a few words; we
                              will find the solution much quickly: i.e. we need
                              tax-included price for this link.)
                              <TextInput
                                name="other"
                                onChange={(e: any) => {
                                  setValues({
                                    ...values,
                                    other: e.target.value,
                                  })
                                }}
                                onKeyDown={(e: any) =>
                                  (e.key == ' ' ||
                                    e.code == 'Space' ||
                                    e.keyCode == 32) &&
                                  e.stopPropagation()
                                }
                              />
                            </div>
                          ),
                        },
                      ]}
                    />
                    <div className="flex flex-col space-y-2 items-end">
                      {isError && (
                        <div className="sm:pt-2 sm:pr-4 flex justify-end">
                          <ErrorMessage error={error} />
                        </div>
                      )}
                      <div className="inline-flex float-right gap-x-3 mt-4">
                        <button
                          onClick={onClose}
                          className="border border-solid border-gray-300 shadow-sm rounded-lg h-11 w-1/3 mr-1.5 active:bg-gray-100"
                        >
                          <span className="font-medium text-base text-gray-700 ">
                            Cancel
                          </span>
                        </button>
                        <button
                          disabled={values.problem === '' || isLoading}
                          type="submit"
                          className="border border-solid disabled:bg-gray-300 disabled:border-gray-300 active:border-error-600 bg-error-600 active:bg-error-700 shadow-sm rounded-lg px-3 h-11 w-40"
                        >
                          <span className="font-medium text-base text-white-100 ">
                            {isLoading ? 'Please wait...' : 'Report this URL'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}
          {step === 3 &&
            (!isAlreadyReported ? (
              <div className="flex flex-col   w-96">
                <div className="flex justify-center w-full">
                  <SuccessIcon />
                </div>

                <Dialog.Title className="text-lg font-medium text-gray-900 mt-4 mb-2 flex justify-center">
                  Successfully Reported
                </Dialog.Title>

                <div className="flex justify-center gap-3 mt-8">
                  <PrimaryButton
                    bgColor="600"
                    type="submit"
                    classNameText="px-4 w-full"
                    classNameButton="h-11 w-full"
                    onClick={onClose}
                  >
                    OK
                  </PrimaryButton>
                </div>
              </div>
            ) : (
              <div className="flex flex-col   w-96">
                <div className="flex justify-center w-full">
                  <ClockIcon />
                </div>

                <Dialog.Title className="text-lg font-medium text-gray-900 mt-4 mb-2 flex justify-center">
                  URL Already Reported
                </Dialog.Title>
                <Dialog.Description className="text-sm font-normal text-gray-700 mt-2 mb-4 flex">
                  <div>
                    You've already reported this website. In general, a problem
                    affects all the URLs, so that reporting just one URL is
                    enough. We'll take care of all the URLs in your panel. You
                    can always check the status of your reports on your{' '}
                    <a href="https://app.prisync.com/open-reports">
                      <u>support page</u>
                    </a>
                    .
                  </div>
                </Dialog.Description>
                <div className="flex justify-center gap-3 mt-8">
                  <button
                    onClick={() => navigate('/open-reports')}
                    className="underline w-full"
                  >
                    URL Dashboard
                  </button>
                  <PrimaryButton
                    bgColor="600"
                    type="submit"
                    classNameText="px-4 w-full"
                    classNameButton="h-11 w-full"
                    onClick={onClose}
                  >
                    OK
                  </PrimaryButton>
                </div>
              </div>
            ))}
        </div>
      </Dialog.Panel>
    </div>
  )
}

export default ReportUrlModal
